import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { PhoneService } from '@superdispatch/phones';
import { DateTime } from 'luxon';
import { hasOnlyDigits } from 'shared/utils/StringUtils';
import {
  yupBoolean,
  yupDateString,
  yupNumber,
  yupObject,
  yupPhone,
  yupString,
} from 'shared/utils/YupUtils';
import { z } from 'zod';

function toStringValue(value: unknown): unknown {
  if (value == null) {
    return '';
  }

  return typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : value;
}

function toDateISO(value: unknown): unknown {
  return (
    stringifyDate(value as NullableDateInput, { format: 'DateISO' }) ?? null
  );
}

function toNullableNumber(value: unknown): unknown {
  return value == null || value === '' ? null : Number(value);
}

export const cargoInsuranceYupSchema = yupObject({
  guid: yupString(),
  is_verified: yupBoolean(),
  is_current: yupBoolean(),
  file_url: yupString().required(),
  expiration_date: yupDateString('DateISO')
    .required('Enter Expiration Date')
    .test(
      'validate-date',
      'Expiration Date must be in future',
      (value) =>
        !!value &&
        DateTime.fromJSDate(new Date(value)) > DateTime.local().startOf('day'),
    ),
  cargo_limit: yupNumber()
    .positive('Value must be greater than zero')
    .required('Enter Cargo Limit'),
  deductible_amount: yupNumber().min(0, 'Deductible can not be negative'),
  auto_policy_number: yupString().max(200),
  cargo_policy_number: yupString().max(200),
  agent_name: yupString().max(200),
  agent_phone: yupPhone(),
  agent_email: yupString().max(200).email(),
});

function isFutureDate(value: string): boolean {
  return DateTime.fromJSDate(new Date(value)) > DateTime.local().startOf('day');
}

export type CargoInsuranceDTO = z.infer<typeof cargoInsuranceSchema>;
export const cargoInsuranceSchema = z
  .object({
    guid: z.preprocess(toStringValue, z.string().catch('')),
    is_verified: z.boolean().nullable().catch(false),
    is_current: z.boolean().nullable().catch(false),
    file_url: z.preprocess(toStringValue, z.string().catch('')),
    expiration_date: z.preprocess(toDateISO, z.string().nullable().catch(null)),
    cargo_limit: z.preprocess(
      toNullableNumber,
      z.number().nullable().catch(null),
    ),
    deductible_amount: z.preprocess(
      toNullableNumber,
      z.number().nullable().catch(null),
    ),
    auto_policy_number: z.preprocess(toStringValue, z.string().catch('')),
    cargo_policy_number: z.preprocess(toStringValue, z.string().catch('')),
    agent_name: z.preprocess(toStringValue, z.string().catch('')),
    agent_phone: z.preprocess(toStringValue, z.string().trim().catch('')),
    agent_email: z.preprocess(toStringValue, z.string().catch('')),
  })
  .superRefine(async (values, context) => {
    function addIssue(path: string, message: string) {
      context.addIssue({ code: 'custom', path: [path], message });
    }

    if (!values.file_url) {
      addIssue('file_url', 'This field is required');
    }

    if (!values.expiration_date) {
      addIssue('expiration_date', 'Enter Expiration Date');
    } else if (!isFutureDate(values.expiration_date)) {
      addIssue('expiration_date', 'Expiration Date must be in future');
    }

    if (values.cargo_limit == null) {
      addIssue('cargo_limit', 'Enter Cargo Limit');
    } else if (values.cargo_limit <= 0) {
      addIssue('cargo_limit', 'Value must be greater than zero');
    }

    if (values.deductible_amount != null && values.deductible_amount < 0) {
      addIssue('deductible_amount', 'Deductible can not be negative');
    }

    (
      [
        'auto_policy_number',
        'cargo_policy_number',
        'agent_name',
        'agent_email',
      ] as const
    ).forEach((field) => {
      if (values[field].length > 200) {
        addIssue(field, 'Maximum length is 200 characters');
      }
    });

    if (
      values.agent_email &&
      !z.email().safeParse(values.agent_email).success
    ) {
      addIssue('agent_email', 'Enter a valid email');
    }

    const phoneMessage = await PhoneService.validate(values.agent_phone);

    if (phoneMessage) {
      addIssue('agent_phone', phoneMessage);
    }
  });

export interface TermsAndConditionsDTO {
  terms_and_conditions: string;
}

export const w9YupSchema = yupObject({ w9_form_url: yupString().required() });

export type W9DTO = z.infer<typeof w9Schema>;
export const w9Schema = z.object({
  w9_form_url: z
    .preprocess(toStringValue, z.string().min(1, 'This field is required'))
    .prefault(''),
});

export const transporterPlateRegistrationYupSchema = yupObject({
  file_url: yupString().required(),
});

export type TransporterPlateRegistrationDTO = z.infer<
  typeof transporterPlateRegistrationSchema
>;
export const transporterPlateRegistrationSchema = z.object({
  file_url: z
    .preprocess(toStringValue, z.string().min(1, 'This field is required'))
    .prefault(''),
});

export const stateMotorCarrierPermitYupSchema = yupObject({
  file_url: yupString().required(),
});

export type StateMotorCarrierPermitDTO = z.infer<
  typeof stateMotorCarrierPermitSchema
>;
export const stateMotorCarrierPermitSchema = z.object({
  file_url: z
    .preprocess(toStringValue, z.string().min(1, 'This field is required'))
    .prefault(''),
});

export const businessLicenseYupSchema = yupObject({
  file_url: yupString().required(),
});

export type BusinessLicenseDTO = z.infer<typeof businessLicenseSchema>;
export const businessLicenseSchema = z.object({
  file_url: z
    .preprocess(toStringValue, z.string().min(1, 'This field is required'))
    .prefault(''),
});

export const billingInfoYupSchema = yupObject({
  billing_contact_name: yupString(),
  billing_email: yupString().email(),
  billing_phone_numbers: yupString(),
});

export type BillingInfoDTO = z.infer<typeof billingInfoSchema>;
export const billingInfoSchema = z
  .object({
    billing_contact_name: z.preprocess(toStringValue, z.string().catch('')),
    billing_email: z.preprocess(toStringValue, z.string().catch('')),
    billing_phone_numbers: z.preprocess(toStringValue, z.string().catch('')),
  })
  .superRefine((values, context) => {
    if (
      values.billing_email &&
      !z.email().safeParse(values.billing_email).success
    ) {
      context.addIssue({
        code: 'custom',
        path: ['billing_email'],
        message: 'Enter a valid email',
      });
    }
  });

export const ACHPaymentInfoYupSchema = yupObject({
  bank_name: yupString().required('Please fill out the Bank Name field'),
  bank_routing_number: yupString()
    .required()
    .length(9, 'Routing number must have 9 digits')
    .test('only digits', 'Invalid routing number', hasOnlyDigits),
  bank_account_number: yupString()
    .required()
    .min(7, 'Account number must have more than 6 digits')
    .test('only digits', 'Invalid bank account number', hasOnlyDigits),
  is_superpay_bank_different: yupBoolean(),
  hours_until_update_allowed: yupNumber(),
  voided_check_url: yupString().required(),
});

export const ACHPaymentInfoRestrictedYupSchema = ACHPaymentInfoYupSchema.shape({
  bank_account_number: yupString()
    .required()
    .min(5, 'Account number must have more than 4 digits')
    .max(17, 'Account number must have 17 or less digits')
    .test('only digits', 'Invalid bank account number', hasOnlyDigits),
});

const achPaymentInfoFields = z.object({
  bank_name: z.preprocess(toStringValue, z.string().catch('')),
  bank_routing_number: z.preprocess(toStringValue, z.string().catch('')),
  bank_account_number: z.preprocess(toStringValue, z.string().catch('')),
  is_superpay_bank_different: z.boolean().nullable().catch(false),
  hours_until_update_allowed: z.preprocess(
    toNullableNumber,
    z.number().nullable().catch(null),
  ),
  voided_check_url: z.preprocess(toStringValue, z.string().catch('')),
});

interface AccountNumberLength {
  min: number;
  minMessage: string;
  max?: number;
  maxMessage?: string;
}

function refineACHPaymentInfo(accountNumber: AccountNumberLength) {
  return (
    values: z.infer<typeof achPaymentInfoFields>,
    context: z.RefinementCtx,
  ) => {
    function addIssue(path: string, message: string) {
      context.addIssue({ code: 'custom', path: [path], message });
    }

    if (!values.bank_name) {
      addIssue('bank_name', 'Please fill out the Bank Name field');
    }

    if (!values.bank_routing_number) {
      addIssue('bank_routing_number', 'This field is required');
    }

    if (values.bank_routing_number.length !== 9) {
      addIssue('bank_routing_number', 'Routing number must have 9 digits');
    }

    if (!hasOnlyDigits(values.bank_routing_number)) {
      addIssue('bank_routing_number', 'Invalid routing number');
    }

    if (!values.bank_account_number) {
      addIssue('bank_account_number', 'This field is required');
    }

    if (values.bank_account_number.length < accountNumber.min) {
      addIssue('bank_account_number', accountNumber.minMessage);
    }

    if (
      accountNumber.max != null &&
      accountNumber.maxMessage &&
      values.bank_account_number.length > accountNumber.max
    ) {
      addIssue('bank_account_number', accountNumber.maxMessage);
    }

    if (!hasOnlyDigits(values.bank_account_number)) {
      addIssue('bank_account_number', 'Invalid bank account number');
    }

    if (!values.voided_check_url) {
      addIssue('voided_check_url', 'This field is required');
    }
  };
}

export type ACHPaymentInfoDTO = z.infer<typeof ACHPaymentInfoSchema>;
export const ACHPaymentInfoSchema = achPaymentInfoFields.superRefine(
  refineACHPaymentInfo({
    min: 7,
    minMessage: 'Account number must have more than 6 digits',
  }),
);

export const ACHPaymentInfoRestrictedSchema = achPaymentInfoFields.superRefine(
  refineACHPaymentInfo({
    min: 5,
    minMessage: 'Account number must have more than 4 digits',
    max: 17,
    maxMessage: 'Account number must have 17 or less digits',
  }),
);
